import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CellLineCatalogPolicyPage } from '../pages/CellLineCatalogPolicyPage';
import { CellLineCatalogIdentityHelp } from '../pages/CellLineCatalogIdentityHelp';
import { CellLineCatalogExpansionHelp } from '../pages/CellLineCatalogExpansionHelp';

export type HelpTopic = {
  id: string;
  titleKey: string;
  content: ReactNode;
};

function CultureSourcesHelp() {
  const { i18n } = useTranslation();
  const zh = i18n.resolvedLanguage !== 'en-US';
  return zh ? (
    <div>
      <h3>培养字段来源</h3>
      <p>
        培养字段（生长方式、培养基、血清、培养条件、传代比例、倍增时间、生物安全等级）
        按优先级收集多个官方细胞库来源：ATCC &gt; DSMZ &gt; ECACC &gt; JCRB &gt; RIKEN。
      </p>
      <ul>
        <li>主来源（培养信息主来源）优先，缺失字段自动由备选来源（培养信息备用来源）补全；</li>
        <li>不同来源值全部并列保存并可在详情“培养信息”中切换查看，不静默合并；</li>
        <li>Tier A 表示已核对官方说明书（含版本与链接），Tier B 表示按官方惯例整理、待来源复核。</li>
      </ul>
    </div>
  ) : (
    <div>
      <h3>Culture field sources</h3>
      <p>
        Culture fields (growth mode, medium, serum, conditions, passage ratio, doubling
        time, biosafety level) are collected from multiple official cell banks by priority:
        ATCC &gt; DSMZ &gt; ECACC &gt; JCRB &gt; RIKEN.
      </p>
      <ul>
        <li>The primary source is preferred and the fallback fills missing fields;</li>
        <li>Values from different sources are kept in parallel and switchable in the
          detail view — never silently merged;</li>
        <li>Tier A means verified against an official sheet (with version and link);
          Tier B means organized from official conventions and pending source review.</li>
      </ul>
    </div>
  );
}

function FieldsHelp() {
  const { i18n } = useTranslation();
  const zh = i18n.resolvedLanguage !== 'en-US';
  return zh ? (
    <div>
      <h3>状态字段说明</h3>
      <ul>
        <li><b>记录状态</b>：标准记录是否启用（启用/停用），影响业务侧检索与启用。</li>
        <li><b>AI 评估状态</b>：AI 按评估标准对身份、来源与风险给出的结论（已导入/评估通过/有提示/需人工复核/已拒绝等），决定能否发布。</li>
        <li><b>风险状态</b>：公开记录的误鉴定或交叉污染提示；暂无已知风险不等于实验室实物认证。</li>
        <li><b>审核状态</b>：管理员维护流（草稿/已审核/已退役），退役记录不进入业务选择器。</li>
      </ul>
    </div>
  ) : (
    <div>
      <h3>Status field reference</h3>
      <ul>
        <li><b>Record status</b>: whether the standard record is enabled (active/inactive).</li>
        <li><b>AI evaluation status</b>: conclusion from AI evaluation of identity, sources and
          risks (imported/passed/warning/review required/rejected), gating publishing.</li>
        <li><b>Risk status</b>: public misidentification or cross-contamination notes; no known
          risk is not a laboratory authentication.</li>
        <li><b>Curation status</b>: administrative workflow (draft/approved/retired); retired
          records are excluded from business selectors.</li>
      </ul>
    </div>
  );
}

export const HELP_TOPICS: HelpTopic[] = [
  {
    id: 'catalog-ai',
    titleKey: 'help.catalogAi',
    content: <CellLineCatalogPolicyPage />,
  },
  {
    id: 'culture-sources',
    titleKey: 'help.cultureSources',
    content: <CultureSourcesHelp />,
  },
  {
    id: 'fields',
    titleKey: 'help.fields',
    content: <FieldsHelp />,
  },
  {
    id: 'catalog-identity',
    titleKey: 'help.catalogIdentity',
    content: <CellLineCatalogIdentityHelp />,
  },
  {
    id: 'catalog-expansion',
    titleKey: 'help.catalogExpansion',
    content: <CellLineCatalogExpansionHelp />,
  },
];
